import { motion } from 'framer-motion';
import { useState } from 'react';

export function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      const params = new URLSearchParams();
      params.append('email', email);
      const res = await fetch('https://readdy.ai/api/form/d76ci654hjod66j2eo00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  const quickLinks = [
    { label: '소개', id: 'about' },
    { label: '베스트', id: 'best-tours' },
    { label: '서비스', id: 'services' },
    { label: '차량', id: 'vehicles' },
    { label: '포트폴리오', id: 'portfolio' },
    { label: '후기', id: 'testimonials' },
    { label: '예약', id: 'booking' }
  ];

  const services = [
    '동부 코스 투어',
    '서부 코스 투어',
    '한라산 등반',
    '맛집 투어',
    '전세버스 / VIP 리무진',
    '웨딩 스냅 촬영',
    'CF & 방송 촬영',
    '골프 부킹'
  ];

  const socialLinks = [
    {
      icon: 'ri-kakao-talk-fill',
      label: '카카오채널',
      textColor: 'text-[#3C1E1E]',
      bg: 'bg-[#FEE500] hover:bg-yellow-300',
      href: null,
      isKakao: true,
    },
    {
      icon: 'ri-instagram-line',
      label: '인스타그램',
      textColor: 'text-white',
      bg: 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90',
      href: 'https://instagram.com/jejuvelycom',
      isKakao: false,
    },
    {
      icon: 'ri-youtube-fill',
      label: '유튜브',
      textColor: 'text-white',
      bg: 'bg-[#FF0000] hover:bg-red-600',
      href: 'https://www.youtube.com/@%EC%A0%9C%EC%A3%BC%EB%B8%94%EB%A6%AC',
      isKakao: false,
    },
  ];

  return (
    <footer className="bg-[#1a2236] text-white">
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-[#FF8E53] to-[#ff7a3d]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 sm:py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-white/90 text-sm sm:text-base font-medium mb-1">지금 바로 시작하세요</p>
              <h3 className="text-2xl sm:text-3xl font-bold text-white">당신만의 특별한 제주 여행을 설계해드립니다</h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href="tel:010-5685-7818"
                className="bg-white text-[#FF8E53] px-6 sm:px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-all whitespace-nowrap flex items-center gap-2 justify-center"
              >
                <i className="ri-phone-line text-lg"></i>
                010-5685-7818
              </a>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openKakaoModal'))}
                className="bg-[#FEE500] text-[#3C1E1E] px-6 sm:px-8 py-3 rounded-full font-bold hover:bg-yellow-300 transition-all whitespace-nowrap flex items-center gap-2 justify-center cursor-pointer"
              >
                <i className="ri-kakao-talk-fill text-lg"></i>
                카카오 상담
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">

          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 rounded-full overflow-hidden shadow-lg flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/c1ead532c50398fda057e46e940be938/e04f99b6877b66d4ca7caa0ce01baead.jpeg"
                  alt="제주블리 로고"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  제주블리
                </h3>
                <p className="text-sm text-white/60">Jeju Bully</p>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 text-sm">
              제주 로컬 전문가가 직접 큐레이션하는<br />
              프리미엄 맞춤 투어 서비스.<br />
              진짜 제주를 경험하세요.
            </p>
            <div className="space-y-3">
              <motion.a
                href="tel:010-5685-7818"
                whileHover={{ x: 4 }}
                className="flex items-center gap-3 text-white/80 hover:text-white transition-all group"
              >
                <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-[#FF8E53] transition-all flex-shrink-0">
                  <i className="ri-smartphone-line text-base"></i>
                </div>
                <span className="text-sm font-medium">010-5685-7818</span>
              </motion.a>
              <motion.a
                href="tel:064-782-7818"
                whileHover={{ x: 4 }}
                className="flex items-center gap-3 text-white/80 hover:text-white transition-all group"
              >
                <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-[#0E7FC0] transition-all flex-shrink-0">
                  <i className="ri-phone-line text-base"></i>
                </div>
                <span className="text-sm font-medium">064-782-7818</span>
              </motion.a>
              <motion.a
                href="mailto:jejuvely@gmail.com"
                whileHover={{ x: 4 }}
                className="flex items-center gap-3 text-white/80 hover:text-white transition-all group"
              >
                <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-[#4A7C59] transition-all flex-shrink-0">
                  <i className="ri-mail-line text-base"></i>
                </div>
                <span className="text-sm font-medium">jejuvely@gmail.com</span>
              </motion.a>
              <div className="flex items-center gap-3 text-white/70 group">
                <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-base"></i>
                </div>
                <span className="text-sm">제주시 연미길 32 (오라삼동)</span>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="text-lg font-bold mb-6 flex items-center gap-2 text-white">
              <i className="ri-links-line text-[#FF8E53]"></i>
              빠른 링크
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.li key={index} whileHover={{ x: 4 }}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-white/70 hover:text-white transition-all flex items-center gap-2 group cursor-pointer text-sm"
                  >
                    <i className="ri-arrow-right-s-line text-[#FF8E53] opacity-0 group-hover:opacity-100 transition-opacity"></i>
                    {link.label}
                  </button>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="text-lg font-bold mb-6 flex items-center gap-2 text-white">
              <i className="ri-service-line text-[#0E7FC0]"></i>
              주요 서비스
            </h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <motion.li key={index} whileHover={{ x: 4 }}>
                  <button
                    onClick={() => scrollToSection('services')}
                    className="text-white/70 hover:text-white transition-all flex items-center gap-2 group text-sm cursor-pointer"
                  >
                    <i className="ri-checkbox-circle-line text-[#4A7C59] text-xs opacity-70 group-hover:opacity-100 transition-opacity"></i>
                    {service}
                  </button>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Social & Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h4 className="text-lg font-bold mb-6 flex items-center gap-2 text-white">
              <i className="ri-share-line text-[#FF1493]"></i>
              소셜 미디어
            </h4>
            <p className="text-white/70 mb-5 leading-relaxed text-sm">
              제주블리의 최신 소식과<br />
              특별한 이벤트를 만나보세요
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {socialLinks.map((social, index) => (
                social.isKakao ? (
                  <motion.button
                    key={index}
                    onClick={() => window.dispatchEvent(new CustomEvent('openKakaoModal'))}
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className={`rounded-xl flex items-center gap-2 px-4 h-11 transition-all cursor-pointer ${social.bg}`}
                    title={social.label}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${social.icon} text-lg ${social.textColor}`}></i>
                    </div>
                    <span className={`text-sm font-bold whitespace-nowrap ${social.textColor}`}>{social.label}</span>
                  </motion.button>
                ) : (
                  <motion.a
                    key={index}
                    href={social.href ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className={`rounded-xl flex items-center gap-2 px-4 h-11 transition-all ${social.bg}`}
                    title={social.label}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${social.icon} text-lg ${social.textColor}`}></i>
                    </div>
                    <span className={`text-sm font-bold whitespace-nowrap ${social.textColor}`}>{social.label}</span>
                  </motion.a>
                )
              ))}
            </div>

            {/* Newsletter */}
            <div>
              <h5 className="font-bold mb-3 text-sm text-white">뉴스레터 구독</h5>
              {submitStatus === 'success' ? (
                <div className="flex items-center gap-2 text-[#4A7C59] bg-[#4A7C59]/10 rounded-xl px-4 py-3">
                  <i className="ri-check-line text-lg"></i>
                  <span className="text-sm font-medium">구독 완료! 감사합니다 🎉</span>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="이메일 주소"
                    required
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FF8E53]/50 transition-all text-sm"
                  />
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-[#FF8E53] to-[#ff7a3d] text-white px-4 py-3 rounded-xl font-bold hover:from-[#ff7a3d] hover:to-[#ff6829] transition-all whitespace-nowrap text-sm"
                  >
                    구독하기
                  </motion.button>
                  {submitStatus === 'error' && (
                    <p className="text-red-400 text-xs mt-1">잠시 후 다시 시도해주세요.</p>
                  )}
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Business Info */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-6">
          <div className="text-white/50 text-xs leading-relaxed mb-4 flex flex-wrap gap-x-4 gap-y-1">
            <span><strong className="text-white/70">상호:</strong> 제주블리</span>
            <span><strong className="text-white/70">대표:</strong> 권병주</span>
            <span><strong className="text-white/70">사업자번호:</strong> 556-06-01668</span>
            <span><strong className="text-white/70">통신판매번호:</strong> 제 2021-제주오라-14호</span>
            <span><strong className="text-white/70">주소:</strong> 제주시 연미길 32 (오라삼동)</span>
            <span><strong className="text-white/70">TEL:</strong> 064-782-7818</span>
            <span><strong className="text-white/70">FAX:</strong> 050-4258-7818</span>
            <span><strong className="text-white/70">EMAIL:</strong> jejuvely@gmail.com</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-xs text-center sm:text-left">
              © 2024 제주블리 (Jeju Bully). All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-5 text-xs">
              <button className="text-white/50 hover:text-white/80 transition-all">이용약관</button>
              <button className="text-white/50 hover:text-white/80 transition-all">개인정보처리방침</button>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/50 hover:text-white/80 transition-all flex items-center gap-1"
              >
                <span>Powered by Readdy</span>
                <i className="ri-external-link-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
